import { ArrowLeft, ArrowRight, CalendarClock, Clock, History as HistoryIcon } from 'lucide-react'
import { useEffect, useState } from 'react'

import { cn } from '@/lib/cn'

import { useEvent, useHistory, type City, type Page, type StatusEvent } from './api'
import { EventCard, PastRow, Timeline, Touched } from './Events'
import {
  capital,
  dayKey,
  IMPACT,
  KIND,
  longDay,
  monthOf,
  NOTICE_LEVEL,
  STATE,
  STATUS,
  span,
  tone,
  when,
} from './meta'
import { Overall } from './Overall'
import { Dot, Link, Pill, SectionTitle } from './ui'
import { Uptime } from './Uptime'

function useTitle(title: string) {
  useEffect(() => {
    document.title = title
  }, [title])
}

/** The last days, one by one, with what happened on each — or that nothing did. */
function Recent({ page }: { page: Page }) {
  const today = dayKey(new Date())
  const days: string[] = []
  for (let back = 0; back < page.history_days; back += 1) {
    days.push(dayKey(new Date(Date.now() - back * 86_400_000)))
  }
  const byDay = new Map<string, StatusEvent[]>()
  for (const event of page.recent) {
    const key = dayKey(event.starts_at)
    byDay.set(key, [...(byDay.get(key) ?? []), event])
  }
  // Something that began before the window and ended in it belongs to its first day shown.
  for (const event of page.recent) {
    const key = dayKey(event.starts_at)
    if (!days.includes(key) && event.closed_at) {
      const last = days[days.length - 1]
      byDay.set(last, [...(byDay.get(last) ?? []), event])
    }
  }

  return (
    <section aria-labelledby="passato">
      <SectionTitle
        aside={
          <Link
            to="/cronologia"
            className="text-glow inline-flex items-center gap-1.5 text-[13px] font-medium hover:text-white"
          >
            Cronologia completa
            <ArrowRight className="size-3.5" aria-hidden />
          </Link>
        }
      >
        <span id="passato">Incidenti passati</span>
      </SectionTitle>
      <div className="card divide-y divide-white/[0.06] px-5 sm:px-6">
        {days.map((day) => {
          const events = byDay.get(day) ?? []
          return (
            <div key={day} className="py-4">
              <h3 className="text-fog text-[13px] font-semibold">
                {day === today ? 'Oggi' : capital(longDay(day))}
              </h3>
              {events.length ? (
                <ul className="mt-1">
                  {events.map((event) => (
                    <PastRow key={event.id} event={event} />
                  ))}
                </ul>
              ) : (
                <p className="text-mist/70 mt-1 text-[13px]">Nessun incidente segnalato.</p>
              )}
            </div>
          )
        })}
      </div>
    </section>
  )
}

export function HomePage({
  page,
  city,
  failed,
}: {
  page: Page | undefined
  city: City | undefined
  failed: boolean
}) {
  useTitle(page ? `Stato dei servizi · ${page.name}` : 'Stato dei servizi · Unity RP')
  const now = page ? [...page.notices, ...page.active] : []
  return (
    <div className="space-y-12">
      <div className="space-y-4">
        <Overall page={page} city={city} failed={failed} />
        {now.map((event) => (
          <EventCard key={event.id} event={event} />
        ))}
      </div>

      {page && page.upcoming.length > 0 && (
        <section aria-labelledby="programmate">
          <SectionTitle>
            <span id="programmate" className="inline-flex items-center gap-2">
              <CalendarClock className="text-maint size-4" aria-hidden />
              Manutenzioni programmate
            </span>
          </SectionTitle>
          <div className="space-y-4">
            {page.upcoming.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        </section>
      )}

      {page && <Uptime groups={page.groups} days={page.uptime_days} />}
      {page && <Recent page={page} />}
    </div>
  )
}

function Back() {
  return (
    <Link
      to="/"
      className="text-mist inline-flex items-center gap-1.5 text-[13px] font-medium hover:text-white"
    >
      <ArrowLeft className="size-4" aria-hidden />
      Stato dei servizi
    </Link>
  )
}

function Missing({ what }: { what: string }) {
  useTitle('Non trovato · Stato dei servizi')
  return (
    <div className="card p-8 text-center">
      <p className="font-display text-lg font-semibold">{what}</p>
      <p className="text-mist mt-2 text-sm">
        Il link potrebbe essere sbagliato, o l’evento è stato rimosso.
      </p>
      <div className="mt-5">
        <Back />
      </div>
    </div>
  )
}

export function NotFoundPage() {
  return <Missing what="Questa pagina non esiste" />
}

export function EventPage({ id }: { id: number }) {
  const { data: event, missing, failed } = useEvent(id)
  useTitle(event ? `${event.title} · Stato dei servizi` : 'Stato dei servizi')
  if (missing) return <Missing what="Questo evento non esiste" />
  if (!event) {
    return (
      <div className="space-y-6">
        <Back />
        <div className="card h-64 animate-pulse" aria-busy={!failed} />
      </div>
    )
  }

  const look = tone(event)
  const closed = Boolean(event.closed_at)
  return (
    <div className="animate-rise space-y-6">
      <Back />
      <article className="card overflow-hidden">
        <div
          className="border-b border-white/[0.06] p-6 sm:p-8"
          style={{
            background: `linear-gradient(135deg, color-mix(in srgb, ${STATUS[look].hex} 14%, transparent), transparent 60%)`,
          }}
        >
          <p
            className={cn(
              'text-[11px] font-semibold tracking-[0.2em] uppercase',
              STATUS[look].text,
            )}
          >
            {KIND[event.kind]}
            {event.kind === 'notice'
              ? ` · ${NOTICE_LEVEL[event.impact]}`
              : event.kind === 'incident'
                ? ` · ${IMPACT[event.impact]}`
                : ''}
          </p>
          <h1 className="font-display mt-2 text-[clamp(1.3rem,3.6vw,2rem)] leading-tight font-semibold tracking-[-0.02em] text-balance">
            {event.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-3 text-[13px]">
            {event.kind !== 'notice' && (
              <Pill status={look}>
                <Dot
                  status={look}
                  pulse={!closed && event.state !== 'scheduled'}
                  className="size-2"
                />
                {STATE[event.state]}
              </Pill>
            )}
            <span className="text-mist inline-flex items-center gap-1.5">
              <Clock className="size-3.5" aria-hidden />
              {event.kind === 'maintenance' && event.ends_at
                ? `${when(event.starts_at)} → ${when(event.ends_at)}`
                : closed
                  ? `${when(event.starts_at)} · durata ${span(event.starts_at, event.closed_at)}`
                  : `Dal ${when(event.starts_at)}`}
            </span>
          </div>
          {event.components.length > 0 && (
            <div className="mt-5">
              <p className="text-mist mb-2 text-[12px] font-medium">Servizi coinvolti</p>
              <Touched event={event} />
            </div>
          )}
        </div>
        <div className="p-6 sm:p-8">
          <Timeline event={event} />
        </div>
      </article>
    </div>
  )
}

export function HistoryPage() {
  const [page, setPage] = useState(1)
  const { data, failed } = useHistory(page)
  useTitle('Cronologia · Stato dei servizi')

  const months: { month: string; events: StatusEvent[] }[] = []
  for (const event of data?.items ?? []) {
    const month = monthOf(event.starts_at)
    const last = months[months.length - 1]
    if (last?.month === month) last.events.push(event)
    else months.push({ month, events: [event] })
  }

  return (
    <div className="space-y-6">
      <Back />
      <header>
        <h1 className="font-display inline-flex items-center gap-3 text-[clamp(1.3rem,3.6vw,2rem)] font-semibold tracking-[-0.02em]">
          <HistoryIcon className="text-glow size-6" aria-hidden />
          Cronologia
        </h1>
        <p className="text-mist mt-2 text-sm">
          Incidenti e manutenzioni conclusi, dal più recente.
        </p>
      </header>

      {!data ? (
        <div className="card h-48 animate-pulse" aria-busy={!failed} />
      ) : months.length === 0 ? (
        <div className="card text-mist p-8 text-center text-sm">
          Nessun incidente né manutenzione, finora.
        </div>
      ) : (
        months.map(({ month, events }) => (
          <section key={month} className="card px-5 py-4 sm:px-6">
            <h2 className="text-fog border-b border-white/[0.06] pb-3 text-[13px] font-semibold">
              {month}
            </h2>
            <ul className="mt-1">
              {events.map((event) => (
                <PastRow key={event.id} event={event} />
              ))}
            </ul>
          </section>
        ))
      )}

      {data && data.pages > 1 && (
        <nav className="flex items-center justify-between" aria-label="Pagine">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((value) => value - 1)}
            className="text-glow inline-flex items-center gap-1.5 text-[13px] font-medium enabled:hover:text-white disabled:opacity-30"
          >
            <ArrowLeft className="size-4" aria-hidden />
            Più recenti
          </button>
          <span className="text-mist text-[12px] tabular-nums">
            {page} / {data.pages}
          </span>
          <button
            type="button"
            disabled={page >= data.pages}
            onClick={() => setPage((value) => value + 1)}
            className="text-glow inline-flex items-center gap-1.5 text-[13px] font-medium enabled:hover:text-white disabled:opacity-30"
          >
            Meno recenti
            <ArrowRight className="size-4" aria-hidden />
          </button>
        </nav>
      )}
    </div>
  )
}
